import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './ui';
import { ApiError } from '../api/client';

type Status = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string };

/** Состояние сохранения поля: «Сохраняю…», «Сохранено», ошибка у поля. */
function useSave(name?: string) {
  const [st, setSt] = useState<Status>({ kind: 'idle' });
  const t = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(t.current), []);
  const run = async (fn: () => Promise<unknown>) => {
    clearTimeout(t.current);
    setSt({ kind: 'saving' });
    try {
      await fn();
      setSt({ kind: 'saved' });
      t.current = setTimeout(() => setSt({ kind: 'idle' }), 1800);
      return true;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status === 409
            ? 'Карточку изменили в другом окне — данные обновлены'
            : ((name && e.fields[name]) ?? Object.values(e.fields)[0] ?? e.message)
          : 'Не сохранилось. Повторите.';
      setSt({ kind: 'error', message: msg });
      return false;
    }
  };
  const note =
    st.kind === 'saved' ? (
      <span className="fnote ok" role="status">
        Сохранено
      </span>
    ) : st.kind === 'error' ? (
      <span className="fnote err" role="alert">
        {st.message}
      </span>
    ) : st.kind === 'saving' ? (
      <span className="fnote">Сохраняю…</span>
    ) : null;
  return { run, note, error: st.kind === 'error' };
}

interface Base {
  label: ReactNode;
  className?: string;
  /** Имя поля в ответе API — для ошибки валидации у поля. */
  name?: string;
}

/** Поле с сохранением при уходе с поля. Пустая строка отправляется как null, если nullable. */
export function TextField({
  label,
  value,
  onSave,
  type = 'text',
  className = '',
  name,
  placeholder,
  inputMode,
  nullable,
}: Base & {
  value: string | number | null;
  onSave: (v: string | null) => Promise<unknown>;
  type?: string;
  placeholder?: string;
  inputMode?: 'decimal' | 'url' | 'email' | 'tel' | 'numeric' | 'text';
  nullable?: boolean;
}) {
  const initial = value == null ? '' : String(value);
  const [v, setV] = useState(initial);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setV(initial);
  }, [initial]);
  const s = useSave(name);
  const commit = async () => {
    focused.current = false;
    const next = v.trim();
    if (next === initial.trim()) return;
    const ok = await s.run(() => onSave(nullable && next === '' ? null : next));
    if (!ok) setV(initial);
  };
  return (
    <label className={`f ${className}`}>
      {label}
      <input
        type={type}
        value={v}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={s.error || undefined}
        onFocus={() => (focused.current = true)}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && type !== 'textarea') (e.target as HTMLInputElement).blur();
        }}
      />
      {s.note}
    </label>
  );
}

export function AreaField({
  label,
  value,
  onSave,
  rows = 3,
  placeholder,
  className = 'full',
  name,
}: Base & { value: string; onSave: (v: string) => Promise<unknown>; rows?: number; placeholder?: string }) {
  const [v, setV] = useState(value);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setV(value);
  }, [value]);
  const s = useSave(name);
  return (
    <label className={`f ${className}`}>
      {label}
      <textarea
        rows={rows}
        value={v}
        placeholder={placeholder}
        onFocus={() => (focused.current = true)}
        onChange={(e) => setV(e.target.value)}
        onBlur={async () => {
          focused.current = false;
          if (v.trim() === value.trim()) return;
          const ok = await s.run(() => onSave(v.trim()));
          if (!ok) setV(value);
        }}
      />
      {s.note}
    </label>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onSave,
  className = '',
  name,
}: Base & {
  value: T | '' | null;
  options: readonly (readonly [T | '', string])[];
  onSave: (v: T | null) => Promise<unknown>;
}) {
  const s = useSave(name);
  return (
    <label className={`f ${className}`}>
      {label}
      <select
        value={value ?? ''}
        onChange={(e) => void s.run(() => onSave((e.target.value || null) as T | null))}
      >
        {options.map(([k, t]) => (
          <option key={k} value={k}>
            {t}
          </option>
        ))}
      </select>
      {s.note}
    </label>
  );
}

/** Опции для select из словаря подписей. */
export const opts = <T extends string>(labels: Record<T, string>, empty?: string) =>
  [
    ...(empty !== undefined ? [['', empty] as const] : []),
    ...(Object.entries(labels) as [T, string][]).map(([k, v]) => [k, v] as const),
  ] as readonly (readonly [T | '', string])[];

const pad = (n: number) => String(n).padStart(2, '0');
/** Внешний круг — 12, 1…11; внутренний — 00, 13…23 (24 часа без AM/PM, как на Android). */
const OUTER = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const INNER = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const MIN_LABELS = Array.from({ length: 12 }, (_, i) => i * 5);
const DIAL = 240;
const C = DIAL / 2;
const R_OUT = 98;
const R_IN = 64;
const R_MIN = 98;

/** Точка на циферблате: шаг 0…11 по 30°, 0 — сверху, по часовой стрелке. */
const at = (step: number, r: number) => {
  const a = (step * Math.PI) / 6;
  return { x: C + r * Math.sin(a), y: C - r * Math.cos(a) };
};

/**
 * Время на циферблате, как в часах: сначала час (внешний круг 1–12, внутренний 13–23 и 00),
 * затем минуты (метки через 5 минут, перетаскиванием — любая минута). «Готово» сохраняет.
 */
export function TimeField({
  label,
  value,
  onSave,
  className = '',
  name,
}: Base & { value: string | null; onSave: (v: string | null) => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'h' | 'm'>('h');
  const [h, setH] = useState(9);
  const [m, setM] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const dial = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const s = useSave(name);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const start = () => {
    setH(value ? Number(value.slice(0, 2)) : 9);
    setM(value ? Number(value.slice(3, 5)) : 0);
    setMode('h');
    setOpen(!open);
  };
  const save = (v: string | null) => {
    setOpen(false);
    if (v !== value) void s.run(() => onSave(v));
  };

  /** Указатель на циферблате → час (по кругу и расстоянию) или минута. */
  const pick = (clientX: number, clientY: number, final: boolean) => {
    const r = dial.current?.getBoundingClientRect();
    if (!r) return;
    const dx = clientX - r.left - r.width / 2;
    const dy = clientY - r.top - r.height / 2;
    const scale = r.width / DIAL;
    const dist = Math.hypot(dx, dy) / scale;
    const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const angle = (deg + 360) % 360;
    if (mode === 'h') {
      const step = Math.round(angle / 30) % 12;
      setH(dist < (R_OUT + R_IN) / 2 ? INNER[step]! : OUTER[step]!);
      if (final) setMode('m');
    } else setM(Math.round(angle / 6) % 60);
  };

  // стрелка: к выбранному часу или минуте
  const hand =
    mode === 'h'
      ? (() => {
          const inner = INNER.includes(h);
          const step = (inner ? INNER : OUTER).indexOf(h);
          return { ...at(step, inner ? R_IN : R_OUT), r: 17 };
        })()
      : { ...at(m / 5, R_MIN), r: 17 };

  return (
    <div className={`f tf ${className}`} ref={box}>
      <span>{label}</span>
      <button
        type="button"
        className="tf-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={s.error || undefined}
        onClick={start}
      >
        <Icon name="clock" size={16} /> {value || 'Не задано'}
      </button>
      {open && (
        <div className="tf-pop" role="dialog" aria-label={typeof label === 'string' ? label : 'Время'}>
          <div className="tf-head">
            <button type="button" aria-pressed={mode === 'h'} aria-label="Часы" onClick={() => setMode('h')}>
              {pad(h)}
            </button>
            <span aria-hidden="true">:</span>
            <button
              type="button"
              aria-pressed={mode === 'm'}
              aria-label="Минуты"
              onClick={() => setMode('m')}
            >
              {pad(m)}
            </button>
          </div>
          <div className="tf-hint">{mode === 'h' ? 'Выберите час' : 'Выберите минуты'}</div>
          <div
            className="tf-dial"
            ref={dial}
            onPointerDown={(e) => {
              if ((e.target as HTMLElement).closest('button')) return;
              dragging.current = true;
              (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
              pick(e.clientX, e.clientY, false);
            }}
            onPointerMove={(e) => dragging.current && pick(e.clientX, e.clientY, false)}
            onPointerUp={(e) => {
              if (!dragging.current) return;
              dragging.current = false;
              pick(e.clientX, e.clientY, true);
            }}
          >
            <svg className="tf-svg" viewBox={`0 0 ${DIAL} ${DIAL}`} aria-hidden="true">
              <circle cx={C} cy={C} r={3.5} className="tf-center" />
              <line x1={C} y1={C} x2={hand.x} y2={hand.y} className="tf-hand" />
              <circle cx={hand.x} cy={hand.y} r={hand.r} className="tf-knob" />
            </svg>
            {mode === 'h'
              ? [
                  ...OUTER.map((v, i) => ({ v, ...at(i, R_OUT), inner: false })),
                  ...INNER.map((v, i) => ({ v, ...at(i, R_IN), inner: true })),
                ].map(({ v, x, y, inner }) => (
                  <button
                    key={v}
                    type="button"
                    className={`tf-num ${inner ? 'in' : ''}`}
                    style={{ left: `${(x / DIAL) * 100}%`, top: `${(y / DIAL) * 100}%` }}
                    aria-pressed={v === h}
                    aria-label={`${pad(v)} ч`}
                    onClick={() => {
                      setH(v);
                      setMode('m');
                    }}
                  >
                    {inner ? pad(v) : v}
                  </button>
                ))
              : MIN_LABELS.map((v, i) => {
                  const { x, y } = at(i, R_MIN);
                  return (
                    <button
                      key={v}
                      type="button"
                      className="tf-num"
                      style={{ left: `${(x / DIAL) * 100}%`, top: `${(y / DIAL) * 100}%` }}
                      aria-pressed={v === m}
                      aria-label={`${pad(v)} мин`}
                      onClick={() => setM(v)}
                    >
                      {pad(v)}
                    </button>
                  );
                })}
          </div>
          <div className="tf-foot">
            {value && (
              <button type="button" className="btn sm ghost" onClick={() => save(null)}>
                Без времени
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>
              Отмена
            </button>
            <button type="button" className="btn sm primary" onClick={() => save(`${pad(h)}:${pad(m)}`)}>
              Готово
            </button>
          </div>
        </div>
      )}
      {s.note}
    </div>
  );
}
